/* =============================================
   THEME.JS — Minimal Shopify Theme
   Vanilla JS, no dependencies
   ============================================= */

'use strict';

/* --- Helpers --- */
function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return window.moneyFormat
    ? window.moneyFormat.replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
    : `$${amount}`;
}

function fetchConfig(type = 'javascript') {
  return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: `application/${type}` } };
}

/* --- Cart State --- */
const CartState = {
  _cart: null,
  _listeners: [],

  get() { return this._cart; },
  set(cart) {
    this._cart = cart;
    this._listeners.forEach(fn => fn(cart));
    this.updateCountBadges(cart.item_count);
  },
  onChange(fn) { this._listeners.push(fn); },

  updateCountBadges(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.dataset.count = count;
    });
  },

  async fetch() {
    const res = await fetch(window.routes.cart_url + '.js');
    const cart = await res.json();
    this.set(cart);
    return cart;
  },

  async addItem(variantId, qty = 1, properties = {}) {
    const config = fetchConfig();
    config.body = JSON.stringify({ items: [{ id: variantId, quantity: qty, properties }] });
    const res = await fetch(window.routes.cart_add_url + '.js', config);
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Error adding item');
    }
    await this.fetch();
    this.syncTiers(this._cart);   // background: don't hold up the add
    return this._cart;
  },

  /* Keep quantity-tier discount codes in step with the cart (cs-cart-core's
     csReconcileCart). Stores the fixed cart; returns true when the codes on
     the cart changed, so a server-rendered cart page knows to reload. */
  async syncTiers(cart) {
    if (typeof window.csReconcileCart !== 'function') return false;
    const res = await window.csReconcileCart(cart);
    if (res && res.cart && res.changed) this.set(res.cart);
    return !!(res && res.changed);
  },

  async updateItem(key, qty) {
    const config = fetchConfig();
    config.body = JSON.stringify({ id: key, quantity: qty });
    const res = await fetch(window.routes.cart_change_url + '.js', config);
    const cart = await res.json();
    this.set(cart);
    this.lastSyncChanged = await this.syncTiers(cart);
    return this._cart;
  }
};

/* --- Cart Drawer --- */
class CartDrawer {
  constructor() {
    this.el = document.getElementById('cart-drawer');
    this.body = document.getElementById('cart-drawer-body');
    this.footer = document.getElementById('cart-drawer-footer');
    if (!this.el) return;

    this.el.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());
    this.el.querySelector('.cart-drawer__overlay')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close();
    });

    CartState.onChange(() => this.render());
  }

  open() {
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.render();
    setTimeout(() => this.el.querySelector('.cart-drawer__close')?.focus(), 50);
  }

  close() {
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  render() {
    const cart = CartState.get();
    if (!cart) { this.body.innerHTML = '<div class="loading"><div class="spinner"></div></div>'; return; }

    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          <p>Your cart is empty</p>
          <a href="/collections/all" class="btn btn-secondary btn-sm">Continue shopping</a>
        </div>`;
      this.footer.innerHTML = '';
      return;
    }

    this.body.innerHTML = `
      <ul class="cart-items">
        ${cart.items.map(item => `
          <li class="cart-item">
            <div class="cart-item__img">
              ${item.image
                ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">`
                : '<div style="aspect-ratio:1;background:var(--color-border)"></div>'}
            </div>
            <div class="cart-item__body">
              <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
              ${item.variant_title && item.variant_title !== 'Default Title'
                ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
              <div class="cart-item__footer">
                <div class="qty-wrap cart-item__qty">
                  <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                  <input class="qty-input" type="number" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Quantity">
                  <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
                </div>
                <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
              </div>
              <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove ${item.product_title}">Remove</button>
            </div>
          </li>`).join('')}
      </ul>`;

    this.footer.innerHTML = `
      <div class="cart-footer__subtotal">
        <span>Subtotal</span>
        <span>${formatMoney(cart.total_price)}</span>
      </div>
      <a href="/cart" class="btn btn-primary btn-full">View cart</a>
      <form action="/cart" method="post">
        <button type="submit" name="checkout" class="btn btn-secondary btn-full" style="margin-top:0.75rem">Checkout</button>
      </form>
      <p class="cart-footer__note">Taxes and shipping calculated at checkout</p>`;

    // Bind qty events
    this.body.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const input = this.body.querySelector(`.qty-input[data-key="${key}"]`);
        let qty = parseInt(input.value);
        qty += btn.dataset.action === 'increase' ? 1 : -1;
        qty = Math.max(0, qty);
        input.value = qty;
        CartState.updateItem(key, qty);
      });
    });

    this.body.querySelectorAll('.qty-input').forEach(input => {
      input.addEventListener('change', () => {
        CartState.updateItem(input.dataset.key, Math.max(0, parseInt(input.value) || 0));
      });
    });

    this.body.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', () => CartState.updateItem(btn.dataset.key, 0));
    });
  }
}

/* --- Add to Cart --- */
class AddToCartForm {
  constructor(form) {
    this.form = form;
    this.btn = form.querySelector('[data-add-to-cart]');
    this.errorEl = form.querySelector('[data-cart-error]');
    this.form.addEventListener('submit', e => this.onSubmit(e));
  }

  async onSubmit(e) {
    e.preventDefault();
    if (!this.btn || this.btn.disabled) return;

    const variantId = this.form.querySelector('[name="id"]')?.value;
    const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value || 1);
    if (!variantId) return;

    this.btn.disabled = true;
    const original = this.btn.textContent;
    this.btn.textContent = 'Adding...';
    if (this.errorEl) this.errorEl.textContent = '';

    try {
      await CartState.addItem(variantId, qty);

      // Post-add behavior (theme setting: drawer / page / none).
      // CartState.addItem already refreshed the header count badge.
      const behavior = window.cartAddBehavior || 'drawer';
      if (behavior === 'page') {
        window.location.href = window.routes.cart_url;
      } else if (behavior === 'drawer') {
        document.dispatchEvent(new CustomEvent('cs:cart:open'));
      }
    } catch (err) {
      if (this.errorEl) this.errorEl.textContent = err.message;
    } finally {
      this.btn.disabled = false;
      this.btn.textContent = original;
    }
  }
}

/* --- Variant Selector --- */
class VariantSelector {
  constructor(container) {
    this.container = container;
    this.productData = JSON.parse(container.dataset.productJson || '{}');
    this.variants = this.productData.variants || [];
    this.options = this.productData.options || [];
    this.currentVariant = null;

    this.priceEl = container.querySelector('[data-variant-price]');
    this.comparePriceEl = container.querySelector('[data-variant-compare-price]');
    this.stockEl = container.querySelector('[data-variant-stock]');
    this.addBtn = container.querySelector('[data-add-to-cart]');
    this.idInput = container.querySelector('[name="id"]');
    // A metafield-driven gallery ([data-custom-images]) isn't tied to variant
    // media, so leave its main image alone on variant change.
    const gallery = container.closest('.product-layout')?.querySelector('.product-gallery');
    this.mainImg = gallery?.hasAttribute('data-custom-images')
      ? null
      : gallery?.querySelector('.product-gallery__main img');

    this.bindSelectors();
    this.setInitialVariant();
  }

  bindSelectors() {
    // Dropdown selects
    this.container.querySelectorAll('[data-variant-option]').forEach(el => {
      el.addEventListener('change', () => this.updateVariant());
    });

    // Pill buttons
    this.container.querySelectorAll('.variant-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        const group = pill.closest('[data-option-group]');
        group?.querySelectorAll('.variant-pill').forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');
        this.updateVariant();
      });
    });
  }

  getSelectedOptions() {
    const selected = [];
    this.container.querySelectorAll('[data-option-position]').forEach(el => {
      if (el.tagName === 'SELECT') {
        selected[parseInt(el.dataset.optionPosition) - 1] = el.value;
      } else {
        const active = el.querySelector('.variant-pill.is-active');
        if (active) selected[parseInt(el.dataset.optionPosition) - 1] = active.dataset.value;
      }
    });
    return selected;
  }

  findVariant(options) {
    return this.variants.find(v =>
      v.options.every((opt, i) => opt === options[i])
    ) || null;
  }

  updateVariant() {
    const options = this.getSelectedOptions();
    this.currentVariant = this.findVariant(options);
    this.updateUI();
    this.updateURL();
  }

  setInitialVariant() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');
    if (variantId) {
      this.currentVariant = this.variants.find(v => v.id == variantId) || this.variants[0];
    } else {
      this.currentVariant = this.variants.find(v => v.available) || this.variants[0];
    }
    if (this.currentVariant) this.setSelectors(this.currentVariant);
    this.updateUI();
  }

  setSelectors(variant) {
    variant.options.forEach((value, i) => {
      const pos = i + 1;
      const select = this.container.querySelector(`select[data-option-position="${pos}"]`);
      if (select) select.value = value;

      const group = this.container.querySelector(`[data-option-position="${pos}"]`);
      if (group) {
        group.querySelectorAll('.variant-pill').forEach(p => {
          p.classList.toggle('is-active', p.dataset.value === value);
        });
      }
    });
  }

  updateUI() {
    const v = this.currentVariant;
    if (!v) return;

    if (this.idInput) this.idInput.value = v.id;

    if (this.priceEl) this.priceEl.textContent = formatMoney(v.price);
    if (this.comparePriceEl) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        this.comparePriceEl.textContent = formatMoney(v.compare_at_price);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    if (this.addBtn) {
      if (v.available) {
        this.addBtn.disabled = false;
        this.addBtn.textContent = 'Add to cart';
      } else {
        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Sold out';
      }
    }

    if (this.stockEl) {
      this.stockEl.textContent = v.available ? '' : 'Out of stock';
      this.stockEl.classList.toggle('hidden', v.available);
    }

    // Update unavailable pills
    this.updateUnavailablePills();

    // Swap featured image if variant has one
    if (v.featured_image && this.mainImg) {
      const newSrc = v.featured_image.src;
      if (newSrc) this.mainImg.src = newSrc;
    }
  }

  updateUnavailablePills() {
    this.container.querySelectorAll('[data-option-position]').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition);
      group.querySelectorAll('.variant-pill').forEach(pill => {
        const testOptions = this.getSelectedOptions().slice();
        testOptions[pos - 1] = pill.dataset.value;
        const matchingVariant = this.findVariant(testOptions);
        pill.classList.toggle('is-unavailable', !matchingVariant || !matchingVariant.available);
      });
    });
  }

  updateURL() {
    if (!this.currentVariant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }
}

/* --- Product Gallery --- */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.main = el.querySelector('.product-gallery__main');
    this.mainImg = this.main?.querySelector('img');
    this.thumbs = Array.from(el.querySelectorAll('.product-gallery__thumb'));
    this.track = el.querySelector('.product-gallery__thumbs');

    // Pagination dots are opt-in: built only when the snippet rendered the
    // container. One dot per image is correct here — the gallery shows exactly
    // one main image at a time, so there is no per-view paging to derive.
    this.dotsWrap = el.querySelector('[data-gallery-dots]');
    this.dots = [];
    this.buildDots();

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.setActive(thumb));
    });

    // Main image arrows cycle through the images (with wrap-around)
    el.querySelectorAll('[data-gallery-arrow]').forEach(a => {
      a.addEventListener('click', () => this.step(a.dataset.galleryArrow));
    });
    // Thumbnail-strip arrows scroll the strip
    el.querySelectorAll('[data-thumb-arrow]').forEach(a => {
      a.addEventListener('click', () => this.scrollThumbs(a.dataset.thumbArrow));
    });
    this.thumbArrows = Array.from(el.querySelectorAll('[data-thumb-arrow]'));
    if (this.thumbArrows.length && this.track) {
      const sync = () => this.syncThumbArrows();
      this.track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      window.addEventListener('load', sync);
      sync();
    }
  }

  // Grey out a thumbnail arrow when the strip can't scroll further that way.
  syncThumbArrows() {
    const t = this.track;
    const horizontal = t.scrollWidth > t.clientWidth + 2;
    const pos = horizontal ? t.scrollLeft : t.scrollTop;
    const max = horizontal ? t.scrollWidth - t.clientWidth : t.scrollHeight - t.clientHeight;
    this.thumbArrows.forEach(a => {
      const off = a.dataset.thumbArrow === 'prev' ? pos <= 1 : pos >= max - 1;
      a.classList.toggle('is-disabled', off);
      a.setAttribute('aria-disabled', off ? 'true' : 'false');
    });
  }

  buildDots() {
    if (!this.dotsWrap || this.thumbs.length < 2) return;
    const label = this.dotsWrap.dataset.dotLabel || '';
    this.dotsWrap.textContent = '';
    this.thumbs.forEach((thumb, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'product-gallery__dot btn-reset' + (i === this.activeIndex() ? ' is-active' : '');
      // Label text comes from Liquid via data-dot-label so it stays translatable.
      d.setAttribute('aria-label', (label + ' ' + (i + 1)).trim());
      d.addEventListener('click', () => this.setActive(thumb));
      this.dotsWrap.appendChild(d);
      this.dots.push(d);
    });
  }

  syncDots() {
    if (!this.dots.length) return;
    const i = this.activeIndex();
    this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
  }

  activeIndex() {
    const i = this.thumbs.findIndex(t => t.classList.contains('is-active'));
    return i < 0 ? 0 : i;
  }

  step(dir) {
    if (this.thumbs.length < 2) return;
    let i = this.activeIndex();
    if (dir === 'prev') i = i <= 0 ? this.thumbs.length - 1 : i - 1;
    else i = i >= this.thumbs.length - 1 ? 0 : i + 1;
    this.setActive(this.thumbs[i]);
  }

  scrollThumbs(dir) {
    if (!this.track) return;
    const horizontal = this.track.scrollWidth > this.track.clientWidth + 2;
    const amount = (horizontal ? this.track.clientWidth : this.track.clientHeight) * 0.7;
    const sign = dir === 'prev' ? -1 : 1;
    this.track.scrollBy(
      horizontal ? { left: sign * amount, behavior: 'smooth' }
                 : { top: sign * amount, behavior: 'smooth' }
    );
  }

  setActive(thumb) {
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    // After the class move, so activeIndex() reads the new position. Covers
    // every entry point — thumb click, arrow step, and dot click.
    this.syncDots();
    if (typeof thumb.scrollIntoView === 'function') {
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    if (!this.mainImg) return;
    const img = thumb.querySelector('img');
    // Prefer the full-size URL rendered by Liquid. Fall back to upgrading the
    // thumbnail URL — handles both modern query-param URLs (?width=200) and the
    // legacy filename format (_200x200.jpg).
    let full = thumb.dataset.fullSrc;
    if (!full && img) {
      full = /[?&]width=/.test(img.src)
        ? img.src.replace(/([?&]width=)\d+/, '$11200')
        : img.src.replace(/(_\d+x\d*|_\d*x\d+)(\.\w+)/, '$2').replace(/(\.\w+)(\?|$)/, '_1200x$1$2');
    }
    if (full) {
      this.mainImg.src = full;
      this.mainImg.srcset = '';
    }
  }
}

/* --- Mobile Menu --- */
class MobileMenu {
  constructor() {
    this.nav = document.getElementById('mobile-nav');
    this.openBtn = document.getElementById('menu-btn');
    this.closeBtn = document.getElementById('mobile-nav-close');
    if (!this.nav) return;

    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.nav.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.nav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.closeBtn?.focus();
  }

  close() {
    this.nav.classList.remove('is-open');
    document.body.style.overflow = '';
    this.openBtn?.focus();
  }
}

/* --- Search Modal --- */
class SearchModal {
  constructor() {
    this.modal = document.getElementById('search-modal');
    this.input = document.getElementById('search-modal-input');
    this.openBtns = document.querySelectorAll('[data-search-open]');
    this.closeBtn = this.modal?.querySelector('.search-modal__close');
    this.overlay = this.modal?.querySelector('.search-modal__overlay');
    if (!this.modal) return;

    this.openBtns.forEach(btn => btn.addEventListener('click', () => this.open()));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
}

/* --- Collapsible --- */
function initCollapsibles() {
  document.querySelectorAll('.collapsible__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !expanded);
      const bodyId = trigger.getAttribute('aria-controls');
      const body = bodyId ? document.getElementById(bodyId) : trigger.nextElementSibling;
      body?.classList.toggle('is-open', !expanded);
    });
  });
}

/* --- Cart Page Quantity Updates --- */
function initCartPage() {
  const form = document.querySelector('[data-cart-form]');
  if (!form) return;

  form.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      const input = form.querySelector(`.qty-input[data-key="${key}"]`);
      let qty = parseInt(input.value);
      qty += btn.dataset.action === 'increase' ? 1 : -1;
      qty = Math.max(0, qty);
      input.value = qty;
      const cart = await CartState.updateItem(key, qty);
      // A quantity-tier code went on or off: the summary is server-rendered.
      if (CartState.lastSyncChanged) { window.location.reload(); return; }
      updateCartPage(cart);
    });
  });

  form.querySelectorAll('.cart-item__remove').forEach(btn => {
    btn.addEventListener('click', async () => {
      const row = btn.closest('.cart-page-item');
      row?.remove();
      const cart = await CartState.updateItem(btn.dataset.key, 0);
      if (CartState.lastSyncChanged) { window.location.reload(); return; }
      updateCartPage(cart);
    });
  });
}

function updateCartPage(cart) {
  const totalEl = document.querySelector('[data-cart-total]');
  if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
}

/* --- Init --- */
document.addEventListener('DOMContentLoaded', () => {
  // Fetch initial cart state
  CartState.fetch();

  // Cart drawer
  if (document.getElementById('cart-drawer')) {
    window.cartDrawer = new CartDrawer();
  }

  // Cart icon opens drawer
  document.querySelectorAll('[data-cart-open]').forEach(btn => {
    btn.addEventListener('click', e => {
      e.preventDefault();
      if (window.cartType === 'drawer') {
        window.cartDrawer?.open();
      } else {
        window.location.href = window.routes.cart_url;
      }
    });
  });

  // Add to cart forms
  document.querySelectorAll('[data-product-form]').forEach(form => {
    new AddToCartForm(form);
  });

  // Variant selectors
  document.querySelectorAll('[data-variant-selector]').forEach(el => {
    new VariantSelector(el);
  });

  // Product galleries
  document.querySelectorAll('.product-gallery').forEach(el => {
    new ProductGallery(el);
  });

  // Exposed so a gallery that re-renders its own DOM (the per-variant gallery in
  // snippets/product-gallery.liquid) can hand the fresh nodes back to the one
  // controller instead of hand-rolling a second set of thumb/arrow handlers.
  window.CSProductGallery = ProductGallery;

  // Mobile menu
  new MobileMenu();

  // Search modal
  new SearchModal();

  // Collapsibles
  initCollapsibles();

  // Cart page
  initCartPage();
});

/* The cart registry (csPrimary, csAddonLines, csAddonPermalinkSuffix,
   csCartLines, csCheckoutUrl, csCartTotal, csFormatMoney) lives in
   snippets/cs-cart-core.liquid, rendered in <head> so blocks can use it on
   first paint. */
